import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getBiosite, updateBiosite, getStats, listBookings, updateBooking, deleteBooking, THEMES, resolveTheme, shapeRadius } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Plus, Trash2, Save, ExternalLink, QrCode, Eye, Loader2, Utensils, BarChart3, MessageCircle, Instagram, Youtube, Facebook, MapPin, Phone, Star, Calendar, Link2, ShoppingBag, Clock, Share2, CalendarDays, CheckCircle2, XCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import PixModal from '../components/PixModal';
import FileUpload from '../components/FileUpload';
import BusinessHoursEditor from '../components/BusinessHoursEditor';
import ShareBiosite from '../components/ShareBiosite';
import ShowcaseEditor from '../components/ShowcaseEditor';
import AgendaPanel from '../components/AgendaPanel';

const SOCIAL_PLATFORMS = ['instagram', 'tiktok', 'youtube', 'twitter', 'facebook', 'globe'];
const PIX_KEY_TYPES = ['cpf', 'cnpj', 'email', 'phone', 'random'];

const LINK_TYPES = [
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'tiktok', label: 'TikTok', icon: Youtube },
  { value: 'facebook', label: 'Facebook', icon: Facebook },
  { value: 'youtube', label: 'YouTube', icon: Youtube },
  { value: 'maps', label: 'Google Maps', icon: MapPin },
  { value: 'phone', label: 'Telefone', icon: Phone },
  { value: 'review', label: 'Avaliar no Google', icon: Star },
  { value: 'menu', label: 'Cardápio', icon: Utensils },
  { value: 'appointment', label: 'Agendamento', icon: Calendar },
  { value: 'custom', label: 'Link personalizado', icon: Link2 },
];
const TYPE_DEFAULTS = {
  whatsapp: { title: 'WhatsApp', icon: 'message-circle' },
  instagram: { title: 'Instagram', icon: 'camera', urlPlaceholder: 'https://instagram.com/seuusuario' },
  tiktok: { title: 'TikTok', icon: 'music', urlPlaceholder: 'https://tiktok.com/@seuusuario' },
  facebook: { title: 'Facebook', icon: 'facebook', urlPlaceholder: 'https://facebook.com/suapagina' },
  youtube: { title: 'YouTube', icon: 'youtube', urlPlaceholder: 'https://youtube.com/@canal' },
  maps: { title: 'Como chegar', icon: 'map-pin', urlPlaceholder: 'https://maps.google.com/?q=endereco' },
  phone: { title: 'Ligar', icon: 'phone' },
  review: { title: 'Avalie no Google', icon: 'star', urlPlaceholder: 'https://g.page/r/.../review' },
  menu: { title: 'Ver Cardápio', icon: 'utensils' },
  appointment: { title: 'Agendar horário', icon: 'calendar' },
  custom: { title: 'Novo link', icon: 'link', urlPlaceholder: 'https://...' },
};

function uid() { return Math.random().toString(36).slice(2, 10); }
function brl(n) { return `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`; }

export default function AdminEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await getBiosite(slug);
        if (!d) { navigate('/admin'); return; }
        d.buttonShape = d.buttonShape || 'rounded';
        d.menu = d.menu || []; d.menuEnabled = d.menuEnabled || false;
        d.menuButtonLabel = d.menuButtonLabel || 'Ver Cardápio';
        d.whatsappForOrders = d.whatsappForOrders || '';
        d.cover = d.cover || ''; d.customTheme = d.customTheme || null;
        d.active = d.active !== false;
        d.pixEnabled = d.pixEnabled !== false;
        d.businessHours = d.businessHours || { enabled: false, timezone: 'America/Sao_Paulo', days: {} };
        d.scheduling = d.scheduling || { enabled: false, buttonLabel: 'Agendar Horário', minHoursAhead: 2, maxDaysAhead: 30, slotStepMin: 30, workingHours: d.businessHours, services: [], confirmationWhatsapp: '', address: '', googleCalendar: { exportEnabled: true, importUrl: '' } };
        d.scheduling.googleCalendar = d.scheduling.googleCalendar || { exportEnabled: true, importUrl: '' };
        d.showcase = d.showcase || { enabled: false, title: 'Serviços & Produtos', items: [] };
        d.links = (d.links || []).map((l) => ({ type: 'custom', phone: '', message: '', enabled: true, ...l }));
        d.pix = (d.pix || []).map((p) => ({ enabled: true, rawCode: '', ...p }));
        setData(d);
      } catch { navigate('/admin'); }
      finally { setLoading(false); }
    })();
  }, [slug, navigate]);

  if (loading || !data) {
    return <div className="min-h-screen grid place-items-center bg-[#faf7f2]"><Loader2 className="animate-spin text-neutral-400" /></div>;
  }

  const update = (patch) => setData((prev) => ({ ...prev, ...patch }));
  const save = async () => {
    setSaving(true);
    try { await updateBiosite(slug, data); toast({ title: 'Alterações salvas!' }); }
    catch { toast({ title: 'Erro ao salvar.' }); }
    finally { setSaving(false); }
  };

  const addLink = (type = 'custom') => {
    const def = TYPE_DEFAULTS[type] || TYPE_DEFAULTS.custom;
    update({ links: [...data.links, { id: uid(), title: def.title, url: '', icon: def.icon, style: 'solid', type, phone: '', message: '', enabled: true }] });
  };
  const updLink = (i, patch) => { const links = [...data.links]; links[i] = { ...links[i], ...patch }; update({ links }); };
  const delLink = (i) => update({ links: data.links.filter((_, idx) => idx !== i) });
  const moveLink = (i, dir) => { const links = [...data.links]; const j = i + dir; if (j < 0 || j >= links.length) return; [links[i], links[j]] = [links[j], links[i]]; update({ links }); };

  const addPix = () => update({ pix: [...(data.pix || []), { id: uid(), title: 'Novo PIX', key: '', keyType: 'email', amount: 0, merchantName: data.name, merchantCity: 'SAO PAULO', enabled: true, rawCode: '' }] });
  const updPix = (i, patch) => { const pix = [...data.pix]; pix[i] = { ...pix[i], ...patch }; update({ pix }); };
  const delPix = (i) => update({ pix: data.pix.filter((_, idx) => idx !== i) });

  const addSocial = () => update({ socials: [...(data.socials || []), { platform: 'instagram', url: 'https://' }] });
  const updSocial = (i, patch) => { const socials = [...data.socials]; socials[i] = { ...socials[i], ...patch }; update({ socials }); };
  const delSocial = (i) => update({ socials: data.socials.filter((_, idx) => idx !== i) });

  const addCategory = () => update({ menu: [...(data.menu || []), { id: uid(), name: 'Nova categoria', products: [] }] });
  const updCategory = (ci, patch) => { const menu = [...data.menu]; menu[ci] = { ...menu[ci], ...patch }; update({ menu }); };
  const delCategory = (ci) => update({ menu: data.menu.filter((_, idx) => idx !== ci) });
  const addProduct = (ci) => { const menu = [...data.menu]; menu[ci] = { ...menu[ci], products: [...(menu[ci].products || []), { id: uid(), name: 'Novo produto', description: '', price: 0, salePrice: 0, image: '', enabled: true }] }; update({ menu }); };
  const updProduct = (ci, pi, patch) => { const menu = [...data.menu]; const products = [...menu[ci].products]; products[pi] = { ...products[pi], ...patch }; menu[ci] = { ...menu[ci], products }; update({ menu }); };
  const delProduct = (ci, pi) => { const menu = [...data.menu]; menu[ci] = { ...menu[ci], products: menu[ci].products.filter((_, idx) => idx !== pi) }; update({ menu }); };

  const updSch = (patch) => update({ scheduling: { ...data.scheduling, ...patch } });
  const addService = () => updSch({ services: [...(data.scheduling.services || []), { id: uid(), name: 'Novo serviço', description: '', durationMin: 30, price: 0, image: '', enabled: true }] });
  const updService = (i, patch) => { const services = [...data.scheduling.services]; services[i] = { ...services[i], ...patch }; updSch({ services }); };
  const delService = (i) => updSch({ services: data.scheduling.services.filter((_, idx) => idx !== i) });

  const theme = resolveTheme(data);
  const radius = shapeRadius(data.buttonShape);

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <nav className="bg-white border-b border-neutral-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900"><ArrowLeft size={14} /> Dashboard</Link>
          <div className="flex items-center gap-2">
            <a href={`/${slug}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="gap-1.5 border-neutral-300"><ExternalLink size={14} /> Abrir página</Button>
            </a>
            <Button onClick={save} disabled={saving} size="sm" className="gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-white">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Salvar
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-8 grid lg:grid-cols-[1fr_360px] gap-8">
        <div>
          <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl font-display font-bold mb-1">Editando /{slug}</h1>
              <p className="text-sm text-neutral-500">Alterações são aplicadas no preview em tempo real.</p>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-xl border border-neutral-200 px-3 py-2">
              <Switch checked={data.active !== false} onCheckedChange={(v) => update({ active: v })} />
              <span className="text-sm font-medium">{data.active === false ? 'Perfil pausado' : 'Perfil ativo'}</span>
            </div>
          </div>

          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="bg-white border border-neutral-200 flex flex-wrap h-auto">
              <TabsTrigger value="profile">Perfil</TabsTrigger>
              <TabsTrigger value="links">Botões ({data.links.length})</TabsTrigger>
              <TabsTrigger value="pix">PIX ({data.pix?.length || 0})</TabsTrigger>
              <TabsTrigger value="socials">Redes ({data.socials?.length || 0})</TabsTrigger>
              <TabsTrigger value="showcase">Mostruário</TabsTrigger>
              <TabsTrigger value="menu">Cardápio</TabsTrigger>
              <TabsTrigger value="scheduling">Agendamento</TabsTrigger>
              <TabsTrigger value="agenda">Agenda</TabsTrigger>
              <TabsTrigger value="hours">Horários</TabsTrigger>
              <TabsTrigger value="theme">Tema</TabsTrigger>
              <TabsTrigger value="share">Compartilhar</TabsTrigger>
              <TabsTrigger value="stats">Estatísticas</TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="bg-white rounded-2xl border border-neutral-200 p-6 mt-4 space-y-5">
              <div>
                <Label className="mb-1 block">Foto de capa</Label>
                <FileUpload value={data.cover} onChange={(v) => update({ cover: v })} label="Enviar capa" aspect="cover" maxW={1600} />
              </div>
              <div>
                <Label className="mb-1 block">Foto de perfil</Label>
                <FileUpload value={data.avatar} onChange={(v) => update({ avatar: v })} label="Enviar foto" aspect="square" maxW={600} />
                <Input value={data.avatar} onChange={(e) => update({ avatar: e.target.value })} className="mt-2" placeholder="Ou cole uma URL de imagem" />
              </div>
              <div>
                <Label>Nome</Label>
                <Input value={data.name} onChange={(e) => update({ name: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Bio</Label>
                <Textarea value={data.bio} onChange={(e) => update({ bio: e.target.value })} rows={2} className="mt-1" />
              </div>
              <div className="flex items-center justify-between pt-2">
                <div><Label>Perfil verificado</Label><p className="text-xs text-neutral-500">Exibe um selo azul ao lado do nome.</p></div>
                <Switch checked={data.verified} onCheckedChange={(v) => update({ verified: v })} />
              </div>
            </TabsContent>

            <TabsContent value="links" className="mt-4 space-y-3">
              {data.links.map((l, i) => {
                const typeDef = TYPE_DEFAULTS[l.type] || TYPE_DEFAULTS.custom;
                return (
                  <div key={l.id} className={`bg-white rounded-2xl border border-neutral-200 p-4 ${l.enabled === false ? 'opacity-60' : ''}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex flex-col gap-1 pt-1">
                        <button onClick={() => moveLink(i, -1)} className="text-neutral-400 hover:text-neutral-900 text-xs">▲</button>
                        <button onClick={() => moveLink(i, 1)} className="text-neutral-400 hover:text-neutral-900 text-xs">▼</button>
                      </div>
                      <div className="flex-1 grid sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Tipo</Label>
                          <Select value={l.type || 'custom'} onValueChange={(v) => updLink(i, { type: v, icon: TYPE_DEFAULTS[v]?.icon || 'link' })}>
                            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>{LINK_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Estilo</Label>
                          <Select value={l.style} onValueChange={(v) => updLink(i, { style: v })}>
                            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="solid">Preenchido</SelectItem><SelectItem value="outline">Contorno</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs">Título (texto do botão)</Label>
                          <Input value={l.title} onChange={(e) => updLink(i, { title: e.target.value })} className="mt-1" />
                        </div>
                        {l.type === 'whatsapp' ? (
                          <>
                            <div><Label className="text-xs">Número (código país+DDD+número)</Label><Input value={l.phone} onChange={(e) => updLink(i, { phone: e.target.value })} placeholder="5511999999999" className="mt-1" /></div>
                            <div><Label className="text-xs">Mensagem automática</Label><Input value={l.message} onChange={(e) => updLink(i, { message: e.target.value })} placeholder="Olá!" className="mt-1" /></div>
                          </>
                        ) : l.type === 'phone' ? (
                          <div className="sm:col-span-2"><Label className="text-xs">Número</Label><Input value={l.phone} onChange={(e) => updLink(i, { phone: e.target.value })} placeholder="11999999999" className="mt-1" /></div>
                        ) : l.type === 'menu' ? (
                          <div className="sm:col-span-2 text-xs text-neutral-500 bg-neutral-50 rounded-lg p-2.5 border border-neutral-200">Este botão abre o cardápio automaticamente. Configure os produtos na aba <b>Cardápio</b>.</div>
                        ) : l.type === 'appointment' ? (
                          <div className="sm:col-span-2 text-xs text-neutral-500 bg-neutral-50 rounded-lg p-2.5 border border-neutral-200">Este botão abre o fluxo de agendamento. Configure serviços e horários na aba <b>Agendamento</b>. (Deixe a URL em branco)</div>
                        ) : (
                          <div className="sm:col-span-2"><Label className="text-xs">URL</Label><Input value={l.url} onChange={(e) => updLink(i, { url: e.target.value })} placeholder={typeDef.urlPlaceholder || 'https://...'} className="mt-1" /></div>
                        )}
                        <div className="sm:col-span-2 flex items-center gap-2 pt-1 text-xs text-neutral-600">
                          <Switch checked={l.enabled !== false} onCheckedChange={(v) => updLink(i, { enabled: v })} />
                          {l.enabled === false ? 'Botão desativado' : 'Botão ativo'}
                        </div>
                      </div>
                      <Button variant="outline" size="icon" onClick={() => delLink(i)} className="border-neutral-300 text-red-600 hover:bg-red-50"><Trash2 size={14} /></Button>
                    </div>
                  </div>
                );
              })}
              <div className="bg-white rounded-2xl border border-dashed border-neutral-300 p-4">
                <div className="text-xs text-neutral-500 mb-2">Adicionar novo botão</div>
                <div className="flex flex-wrap gap-2">
                  {LINK_TYPES.map((t) => (
                    <Button key={t.value} variant="outline" size="sm" onClick={() => addLink(t.value)} className="gap-1.5 border-neutral-300"><t.icon size={14} /> {t.label}</Button>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="pix" className="mt-4 space-y-3">
              <div className="bg-white rounded-2xl border border-neutral-200 p-4 flex items-center justify-between">
                <div><Label>Mostrar botões PIX no biosite</Label><p className="text-xs text-neutral-500">Desative para ocultar todos os botões PIX da página pública.</p></div>
                <Switch checked={data.pixEnabled !== false} onCheckedChange={(v) => update({ pixEnabled: v })} />
              </div>
              {(data.pix || []).map((p, i) => (
                <div key={p.id} className={`bg-white rounded-2xl border border-neutral-200 p-4 ${p.enabled === false ? 'opacity-60' : ''}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 grid sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2"><Label className="text-xs">Título do botão</Label><Input value={p.title} onChange={(e) => updPix(i, { title: e.target.value })} className="mt-1" /></div>
                      <div><Label className="text-xs">Tipo da chave</Label>
                        <Select value={p.keyType} onValueChange={(v) => updPix(i, { keyType: v })}>
                          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{PIX_KEY_TYPES.map((t) => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Chave PIX</Label><Input value={p.key} onChange={(e) => updPix(i, { key: e.target.value })} className="mt-1" /></div>
                      <div><Label className="text-xs">Valor (R$) — 0 = livre</Label><Input type="number" step="0.01" min="0" value={p.amount} onChange={(e) => updPix(i, { amount: parseFloat(e.target.value) || 0 })} className="mt-1" /></div>
                      <div><Label className="text-xs">Nome do recebedor</Label><Input value={p.merchantName} onChange={(e) => updPix(i, { merchantName: e.target.value })} className="mt-1" /></div>
                      <div><Label className="text-xs">Cidade</Label><Input value={p.merchantCity} onChange={(e) => updPix(i, { merchantCity: e.target.value })} className="mt-1" /></div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs">Pix copia e cola (opcional)</Label>
                        <Textarea value={p.rawCode || ''} onChange={(e) => updPix(i, { rawCode: e.target.value })} rows={2} className="mt-1 font-mono text-xs" placeholder="Se você já tem um código gerado pelo banco, cole aqui e ele será usado exatamente como está." />
                      </div>
                      <div className="sm:col-span-2 flex items-center justify-between">
                        <Button variant="outline" size="sm" onClick={() => setPreview(p)} className="gap-1.5 border-neutral-300"><QrCode size={14} /> Testar QR Code</Button>
                        <div className="flex items-center gap-2 text-xs text-neutral-600">
                          <Switch checked={p.enabled !== false} onCheckedChange={(v) => updPix(i, { enabled: v })} />
                          {p.enabled === false ? 'Desativado' : 'Ativo'}
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="icon" onClick={() => delPix(i)} className="border-neutral-300 text-red-600 hover:bg-red-50"><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={addPix} className="w-full gap-2 border-dashed border-neutral-300 h-12"><Plus size={16} /> Adicionar PIX</Button>
            </TabsContent>

            <TabsContent value="socials" className="mt-4 space-y-3">
              {(data.socials || []).map((s, i) => (
                <div key={i} className="bg-white rounded-2xl border border-neutral-200 p-4 flex items-start gap-2">
                  <div className="flex-1 grid sm:grid-cols-[160px_1fr] gap-3">
                    <div><Label className="text-xs">Plataforma</Label>
                      <Select value={s.platform} onValueChange={(v) => updSocial(i, { platform: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{SOCIAL_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">URL</Label><Input value={s.url} onChange={(e) => updSocial(i, { url: e.target.value })} className="mt-1" /></div>
                  </div>
                  <Button variant="outline" size="icon" onClick={() => delSocial(i)} className="border-neutral-300 text-red-600 hover:bg-red-50"><Trash2 size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" onClick={addSocial} className="w-full gap-2 border-dashed border-neutral-300 h-12"><Plus size={16} /> Adicionar rede social</Button>
            </TabsContent>

            <TabsContent value="menu" className="mt-4 space-y-4">
              <div className="bg-white rounded-2xl border border-neutral-200 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div><Label>Ativar cardápio público</Label><p className="text-xs text-neutral-500">Exibe o botão "Ver Cardápio" na página do biosite.</p></div>
                  <Switch checked={!!data.menuEnabled} onCheckedChange={(v) => update({ menuEnabled: v })} />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Título do botão</Label><Input value={data.menuButtonLabel} onChange={(e) => update({ menuButtonLabel: e.target.value })} className="mt-1" /></div>
                  <div><Label className="text-xs">WhatsApp para pedidos</Label><Input value={data.whatsappForOrders} onChange={(e) => update({ whatsappForOrders: e.target.value })} placeholder="5511999999999" className="mt-1" /></div>
                </div>
              </div>
              {(data.menu || []).map((cat, ci) => (
                <div key={cat.id} className="bg-white rounded-2xl border border-neutral-200 p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Input value={cat.name} onChange={(e) => updCategory(ci, { name: e.target.value })} className="flex-1 font-semibold" />
                    <Button variant="outline" size="icon" onClick={() => delCategory(ci)} className="border-neutral-300 text-red-600 hover:bg-red-50"><Trash2 size={14} /></Button>
                  </div>
                  <div className="space-y-3">
                    {(cat.products || []).map((p, pi) => (
                      <div key={p.id} className={`rounded-xl border border-neutral-200 p-3 ${p.enabled === false ? 'opacity-60' : ''}`}>
                        <div className="grid sm:grid-cols-[120px_1fr_auto] gap-3 items-start">
                          <FileUpload value={p.image} onChange={(v) => updProduct(ci, pi, { image: v })} aspect="wide" label="Foto" maxW={800} />
                          <div className="grid grid-cols-2 gap-2">
                            <div className="col-span-2"><Label className="text-xs">Nome</Label><Input value={p.name} onChange={(e) => updProduct(ci, pi, { name: e.target.value })} className="mt-1" /></div>
                            <div className="col-span-2"><Label className="text-xs">Descrição</Label><Textarea rows={2} value={p.description} onChange={(e) => updProduct(ci, pi, { description: e.target.value })} className="mt-1" /></div>
                            <div><Label className="text-xs">Preço (R$)</Label><Input type="number" step="0.01" min="0" value={p.price} onChange={(e) => updProduct(ci, pi, { price: parseFloat(e.target.value) || 0 })} className="mt-1" /></div>
                            <div><Label className="text-xs">Preço promocional</Label><Input type="number" step="0.01" min="0" value={p.salePrice} onChange={(e) => updProduct(ci, pi, { salePrice: parseFloat(e.target.value) || 0 })} className="mt-1" /></div>
                            <div className="col-span-2 flex items-center gap-2 text-xs text-neutral-600 pt-1">
                              <Switch checked={p.enabled !== false} onCheckedChange={(v) => updProduct(ci, pi, { enabled: v })} />
                              {p.enabled === false ? 'Produto desativado' : 'Produto ativo'}
                            </div>
                          </div>
                          <Button variant="outline" size="icon" onClick={() => delProduct(ci, pi)} className="border-neutral-300 text-red-600 hover:bg-red-50 self-start"><Trash2 size={14} /></Button>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" onClick={() => addProduct(ci)} className="w-full gap-2 border-dashed border-neutral-300 h-10"><Plus size={14} /> Adicionar produto em "{cat.name}"</Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={addCategory} className="w-full gap-2 border-dashed border-neutral-300 h-12"><Plus size={16} /> Adicionar categoria</Button>
            </TabsContent>

            <TabsContent value="scheduling" className="mt-4 space-y-4">
              <div className="bg-white rounded-2xl border border-neutral-200 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div><Label>Ativar agendamento público</Label><p className="text-xs text-neutral-500">Exibe o botão "Agendar Horário" no biosite.</p></div>
                  <Switch checked={!!data.scheduling.enabled} onCheckedChange={(v) => updSch({ enabled: v })} />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Título do botão</Label><Input value={data.scheduling.buttonLabel} onChange={(e) => updSch({ buttonLabel: e.target.value })} className="mt-1" /></div>
                  <div><Label className="text-xs">WhatsApp para confirmação</Label><Input value={data.scheduling.confirmationWhatsapp} onChange={(e) => updSch({ confirmationWhatsapp: e.target.value })} placeholder="5511999999999" className="mt-1" /></div>
                  <div><Label className="text-xs">Antecedência mínima (horas)</Label><Input type="number" min="0" value={data.scheduling.minHoursAhead} onChange={(e) => updSch({ minHoursAhead: parseInt(e.target.value) || 0 })} className="mt-1" /></div>
                  <div><Label className="text-xs">Intervalo entre slots (min)</Label><Input type="number" min="5" step="5" value={data.scheduling.slotStepMin} onChange={(e) => updSch({ slotStepMin: parseInt(e.target.value) || 30 })} className="mt-1" /></div>
                  <div className="sm:col-span-2"><Label className="text-xs">Endereço do estabelecimento (aparece na confirmação)</Label><Input value={data.scheduling.address} onChange={(e) => updSch({ address: e.target.value })} className="mt-1" placeholder="Rua Exemplo, 123 - São Paulo" /></div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-neutral-200 p-5">
                <h3 className="font-semibold mb-3">Horários de atendimento (agenda)</h3>
                <BusinessHoursEditor value={data.scheduling.workingHours} onChange={(v) => updSch({ workingHours: v })} title="Disponível para agendamentos" hint="Define os dias e janelas em que os clientes podem agendar." />
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold">Serviços oferecidos</h3>
                {(data.scheduling.services || []).map((s, i) => (
                  <div key={s.id} className={`bg-white rounded-2xl border border-neutral-200 p-4 ${s.enabled === false ? 'opacity-60' : ''}`}>
                    <div className="grid sm:grid-cols-[120px_1fr_auto] gap-3 items-start">
                      <FileUpload value={s.image} onChange={(v) => updService(i, { image: v })} aspect="wide" label="Foto" maxW={800} />
                      <div className="grid grid-cols-2 gap-2">
                        <div className="col-span-2"><Label className="text-xs">Nome do serviço</Label><Input value={s.name} onChange={(e) => updService(i, { name: e.target.value })} className="mt-1" /></div>
                        <div className="col-span-2"><Label className="text-xs">Descrição</Label><Textarea rows={2} value={s.description} onChange={(e) => updService(i, { description: e.target.value })} className="mt-1" /></div>
                        <div><Label className="text-xs">Duração (min)</Label><Input type="number" min="5" step="5" value={s.durationMin} onChange={(e) => updService(i, { durationMin: parseInt(e.target.value) || 30 })} className="mt-1" /></div>
                        <div><Label className="text-xs">Preço (R$)</Label><Input type="number" min="0" step="0.01" value={s.price} onChange={(e) => updService(i, { price: parseFloat(e.target.value) || 0 })} className="mt-1" /></div>
                        <div className="col-span-2 flex items-center gap-2 text-xs text-neutral-600 pt-1">
                          <Switch checked={s.enabled !== false} onCheckedChange={(v) => updService(i, { enabled: v })} />
                          {s.enabled === false ? 'Serviço desativado' : 'Serviço ativo'}
                        </div>
                      </div>
                      <Button variant="outline" size="icon" onClick={() => delService(i)} className="border-neutral-300 text-red-600 hover:bg-red-50 self-start"><Trash2 size={14} /></Button>
                    </div>
                  </div>
                ))}
                <Button variant="outline" onClick={addService} className="w-full gap-2 border-dashed border-neutral-300 h-12"><Plus size={16} /> Adicionar serviço</Button>
              </div>

              <BookingsPanel slug={slug} />

              {/* Google Calendar integration */}
              <div className="bg-white rounded-2xl border border-neutral-200 p-5">
                <h3 className="font-semibold mb-1 flex items-center gap-2"><Calendar size={16} /> Google Calendar</h3>
                <p className="text-xs text-neutral-500 mb-4">Mantenha sua agenda sincronizada automaticamente nos dois sentidos.</p>
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[220px]">
                      <Label>Exportar agenda do biosite (.ics)</Label>
                      <p className="text-xs text-neutral-500">Assine esta URL no Google Calendar (Outras agendas &rarr; Por URL). Todo novo agendamento aparece automaticamente lá.</p>
                    </div>
                    <Switch checked={data.scheduling.googleCalendar?.exportEnabled !== false} onCheckedChange={(v) => updSch({ googleCalendar: { ...data.scheduling.googleCalendar, exportEnabled: v } })} />
                  </div>
                  <IcsExportRow slug={slug} />
                  <div>
                    <Label className="text-xs">URL de bloqueio (iCal p\u00fablico de outra agenda)</Label>
                    <Input value={data.scheduling.googleCalendar?.importUrl || ''}
                      onChange={(e) => updSch({ googleCalendar: { ...data.scheduling.googleCalendar, importUrl: e.target.value } })}
                      placeholder="https://calendar.google.com/calendar/ical/.../basic.ics"
                      className="mt-1 font-mono text-xs" />
                    <p className="text-xs text-neutral-500 mt-1">Cole aqui o endere\u00e7o secreto em iCal da sua agenda do Google. Eventos marcados l\u00e1 bloqueiam automaticamente os hor\u00e1rios aqui.</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="agenda" className="mt-4">
              <AgendaPanel slug={slug} />
            </TabsContent>

            <TabsContent value="showcase" className="mt-4">
              <ShowcaseEditor value={data.showcase} onChange={(v) => update({ showcase: v })} />
            </TabsContent>

            <TabsContent value="hours" className="bg-white rounded-2xl border border-neutral-200 p-6 mt-4">
              <BusinessHoursEditor value={data.businessHours} onChange={(v) => update({ businessHours: v })} />
            </TabsContent>

            <TabsContent value="theme" className="bg-white rounded-2xl border border-neutral-200 p-6 mt-4 space-y-6">
              <div>
                <Label className="mb-3 block">Temas prontos</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {Object.entries(THEMES).map(([key, t]) => (
                    <button key={key} type="button" onClick={() => update({ theme: key, customTheme: null })}
                      className={`relative rounded-xl overflow-hidden h-24 border-2 transition ${data.theme === key && !data.customTheme ? 'border-neutral-900 ring-2 ring-neutral-900/20' : 'border-neutral-200'}`}
                      style={{ background: t.bg }}>
                      <span className="absolute inset-x-0 bottom-0 bg-black/40 text-white text-xs py-1.5 font-medium">{t.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-3 block">Formato dos botões</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[{ value: 'square', label: 'Quadrado', r: '6px' }, { value: 'rounded', label: 'Arredondado', r: '16px' }, { value: 'pill', label: 'Pílula', r: '9999px' }].map((s) => (
                    <button key={s.value} type="button" onClick={() => update({ buttonShape: s.value })}
                      className={`p-3 border-2 transition ${data.buttonShape === s.value ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200'}`} style={{ borderRadius: 12 }}>
                      <div className="h-8 bg-neutral-900" style={{ borderRadius: s.r }} />
                      <div className="text-xs mt-2 font-medium text-center">{s.label}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div><Label>Tema personalizado</Label><p className="text-xs text-neutral-500">Defina suas próprias cores em HEX.</p></div>
                  <Switch checked={!!data.customTheme} onCheckedChange={(v) => update({ customTheme: v ? { bg: '#0f0f10', button: '#ffffff', buttonText: '#0f0f10', text: '#ffffff', bgGradient: '' } : null })} />
                </div>
                {data.customTheme && (
                  <div className="grid sm:grid-cols-2 gap-3 bg-neutral-50 rounded-xl p-4 border border-neutral-200">
                    {[{ k: 'bg', l: 'Cor de fundo' }, { k: 'button', l: 'Cor dos botões' }, { k: 'buttonText', l: 'Texto dos botões' }, { k: 'text', l: 'Cor dos textos' }].map((f) => (
                      <div key={f.k}><Label className="text-xs">{f.l}</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input type="color" value={data.customTheme[f.k] || '#000000'} onChange={(e) => update({ customTheme: { ...data.customTheme, [f.k]: e.target.value } })} className="w-10 h-10 rounded border border-neutral-300 cursor-pointer" />
                          <Input value={data.customTheme[f.k] || ''} onChange={(e) => update({ customTheme: { ...data.customTheme, [f.k]: e.target.value } })} placeholder="#000000" className="flex-1 font-mono text-sm" />
                        </div>
                      </div>
                    ))}
                    <div className="sm:col-span-2"><Label className="text-xs">Gradiente CSS (opcional)</Label>
                      <Input value={data.customTheme.bgGradient || ''} onChange={(e) => update({ customTheme: { ...data.customTheme, bgGradient: e.target.value } })} placeholder="linear-gradient(180deg, #ff9a3c 0%, #c9184a 100%)" className="mt-1 font-mono text-sm" />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="share" className="mt-4">
              <ShareBiosite slug={slug} name={data.name} />
            </TabsContent>

            <TabsContent value="stats" className="mt-4"><StatsPanel slug={slug} /></TabsContent>
          </Tabs>

          <div className="mt-6 flex justify-end">
            <Button onClick={save} disabled={saving} className="gap-2 bg-neutral-900 hover:bg-neutral-800 text-white">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Salvar alterações
            </Button>
          </div>
        </div>

        <aside className="hidden lg:block">
          <div className="sticky top-20">
            <div className="text-xs text-neutral-500 mb-2 flex items-center gap-1.5"><Eye size={12} /> Preview ao vivo</div>
            <div className="w-full rounded-[2rem] border-[10px] border-neutral-900 overflow-hidden shadow-xl" style={{ background: theme.bg }}>
              <div className="h-[600px] overflow-y-auto">
                {data.cover && <div className="h-20 w-full bg-cover bg-center" style={{ backgroundImage: `url(${data.cover})` }} />}
                <div className="p-4 -mt-6 flex flex-col items-center text-center">
                  <img src={data.avatar} className="w-16 h-16 rounded-full object-cover border-[3px] border-white" alt="" />
                  <div className="mt-2 font-bold text-sm" style={{ color: theme.text }}>@{data.slug}</div>
                  <div className="text-xs mt-1 opacity-90 px-2" style={{ color: theme.text }}>{data.bio}</div>
                </div>
                <div className="px-4 pb-4 mt-2 space-y-2">
                  {data.scheduling.enabled && (data.scheduling.services || []).some(s => s.enabled !== false) && (
                    <div className="px-3 py-2.5 text-xs font-semibold text-center" style={{ background: theme.button, color: theme.buttonText, borderRadius: radius }}>{data.scheduling.buttonLabel}</div>
                  )}
                  {data.menuEnabled && (data.menu || []).some(c => (c.products || []).length > 0) && (
                    <div className="px-3 py-2.5 text-xs font-semibold text-center" style={{ background: theme.button, color: theme.buttonText, borderRadius: radius }}>{data.menuButtonLabel || 'Ver Cardápio'}</div>
                  )}
                  {data.links.filter(l => l.enabled !== false).map((l) => (
                    <div key={l.id} className="px-3 py-2.5 text-xs font-semibold text-center"
                      style={{ background: l.style === 'outline' ? 'transparent' : theme.button, color: l.style === 'outline' ? theme.text : theme.buttonText, border: l.style === 'outline' ? `1.5px solid ${theme.text}` : 'none', borderRadius: radius }}>{l.title}</div>
                  ))}
                  {data.pixEnabled !== false && (data.pix || []).filter(p => p.enabled !== false).map((p) => (
                    <div key={p.id} className="px-3 py-2.5 text-xs font-semibold text-center flex justify-center items-center gap-2" style={{ background: theme.button, color: theme.buttonText, borderRadius: radius }}><QrCode size={12} /> {p.title}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {preview && <PixModal pix={preview} merchantName={data.name} onClose={() => setPreview(null)} />}
    </div>
  );
}

function StatsPanel({ slug }) {
  const [range, setRange] = useState('7d');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const s = await getStats(slug, range); setStats(s); } catch { setStats(null); }
      finally { setLoading(false); }
    })();
  }, [slug, range]);
  const clicks = stats?.clicks || {};
  const CARDS = [
    { key: 'views', label: 'Visualizações do biosite', icon: Eye, value: stats?.views || 0 },
    { key: 'whatsapp', label: 'Cliques no WhatsApp', icon: MessageCircle, value: clicks.whatsapp || 0 },
    { key: 'instagram', label: 'Cliques no Instagram', icon: Instagram, value: clicks.instagram || 0 },
    { key: 'pix', label: 'Cliques no PIX', icon: QrCode, value: clicks.pix || 0 },
    { key: 'maps', label: 'Cliques no Google Maps', icon: MapPin, value: clicks.maps || 0 },
    { key: 'menu', label: 'Visualizações do cardápio', icon: Utensils, value: clicks.menu || 0 },
    { key: 'products', label: 'Cliques em produtos', icon: ShoppingBag, value: clicks.products || 0 },
    { key: 'appointment', label: 'Cliques no agendamento', icon: Calendar, value: clicks.appointment || 0 },
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 bg-white rounded-full p-1 border border-neutral-200 w-fit">
        {[{ v: 'today', l: 'Hoje' }, { v: '7d', l: '7 dias' }, { v: '30d', l: '30 dias' }].map((o) => (
          <button key={o.v} onClick={() => setRange(o.v)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${range === o.v ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:text-neutral-900'}`}>{o.l}</button>
        ))}
      </div>
      {loading ? (<div className="grid place-items-center py-16"><Loader2 className="animate-spin text-neutral-400" /></div>) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {CARDS.map((c) => (
            <div key={c.key} className="bg-white rounded-2xl border border-neutral-200 p-4">
              <div className="flex items-center justify-between"><c.icon size={18} className="text-neutral-400" /><BarChart3 size={14} className="text-neutral-300" /></div>
              <div className="mt-3 text-3xl font-display font-bold tracking-tight">{c.value}</div>
              <div className="text-xs text-neutral-500 mt-0.5">{c.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IcsExportRow({ slug }) {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/biosites/${slug}/calendar.ics`;
  const { toast } = useToast();
  const copy = async () => { await navigator.clipboard.writeText(url); toast({ title: 'URL copiada!', description: url }); };
  return (
    <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-200 flex flex-wrap items-center gap-2">
      <div className="flex-1 min-w-[200px] font-mono text-xs text-neutral-700 break-all select-all">{url}</div>
      <Button type="button" size="sm" variant="outline" onClick={copy} className="gap-1.5 border-neutral-300">Copiar URL</Button>
      <a href={url} target="_blank" rel="noopener noreferrer">
        <Button type="button" size="sm" variant="outline" className="gap-1.5 border-neutral-300">Abrir .ics</Button>
      </a>
    </div>
  );
}

function BookingsPanel({ slug }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const refresh = async () => {
    setLoading(true);
    try { const r = await listBookings(slug); setBookings(r || []); } catch { setBookings([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [slug]);
  const setStatus = async (b, status) => {
    try { await updateBooking(slug, b.id, { status }); toast({ title: `Agendamento ${status === 'confirmed' ? 'confirmado' : 'cancelado'}.` }); refresh(); }
    catch { toast({ title: 'Erro.' }); }
  };
  const remove = async (b) => {
    if (!window.confirm('Excluir este agendamento?')) return;
    try { await deleteBooking(slug, b.id); refresh(); } catch { /* ignore */ }
  };
  const grouped = {};
  bookings.forEach((b) => { (grouped[b.date] = grouped[b.date] || []).push(b); });
  const sortedDates = Object.keys(grouped).sort().reverse();

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2"><CalendarDays size={16} /> Agenda</h3>
        <Button variant="outline" size="sm" onClick={refresh} className="gap-1.5 border-neutral-300">Atualizar</Button>
      </div>
      {loading ? <div className="py-6 grid place-items-center"><Loader2 className="animate-spin text-neutral-400" /></div> :
        bookings.length === 0 ? <div className="text-sm text-neutral-500 text-center py-6">Nenhum agendamento ainda.</div> :
        <div className="space-y-3">
          {sortedDates.map((date) => (
            <div key={date}>
              <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-1.5">{date}</div>
              <div className="space-y-2">
                {grouped[date].sort((a, b) => a.time.localeCompare(b.time)).map((b) => (
                  <div key={b.id} className="flex flex-wrap items-center gap-3 bg-neutral-50 border border-neutral-200 rounded-xl p-3 text-sm">
                    <div className="font-mono font-bold text-neutral-900 w-14">{b.time}</div>
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-semibold">{b.customerName} — {b.serviceName}</div>
                      <div className="text-xs text-neutral-500">{b.customerPhone} • {b.durationMin}min {b.price ? `• ${brl(b.price)}` : ''}</div>
                      {b.note && <div className="text-xs text-neutral-500 mt-0.5">“{b.note}”</div>}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${b.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : b.status === 'cancelled' ? 'bg-neutral-200 text-neutral-500' : 'bg-amber-100 text-amber-700'}`}>{b.status}</span>
                      {b.status !== 'confirmed' && <Button size="icon" variant="outline" className="h-7 w-7 border-neutral-300 text-emerald-600 hover:bg-emerald-50" onClick={() => setStatus(b, 'confirmed')}><CheckCircle2 size={14} /></Button>}
                      {b.status !== 'cancelled' && <Button size="icon" variant="outline" className="h-7 w-7 border-neutral-300 text-neutral-600 hover:bg-neutral-100" onClick={() => setStatus(b, 'cancelled')}><XCircle size={14} /></Button>}
                      <Button size="icon" variant="outline" className="h-7 w-7 border-neutral-300 text-red-600 hover:bg-red-50" onClick={() => remove(b)}><Trash2 size={14} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}
